<script lang="ts">
    import { _ } from "svelte-i18n";
    import Dialog, { Title as DTitle, Content as Content, Actions } from "@smui/dialog";
    import Button, { Label } from "@smui/button";
    import { ComparePriceListItem } from "../../../types/ComparePriceList";

    export let open = false;
    export let title;
    export let priceList: ComparePriceListItem[] = [];

    let headerColors = "tescoblue white-text";

    const chartWidth = 980;
    const chartHeight = 500;
    const legendWidth = 220;
    const chartGap = 24;
    const dialogPadding = 60;
    const margin = { top: 24, right: 24, bottom: 40, left: 72 };
    const plotWidth = chartWidth - margin.left - margin.right;
    const plotHeight = chartHeight - margin.top - margin.bottom;
    const yTickCount = 7;
    const averageValueLabelReservedCharacters = 6;
    const averageValueLabelReservedWidth = averageValueLabelReservedCharacters * 7 + 8;
    const averageValueLabelHorizontalGap = averageValueLabelReservedWidth + 12;
    const barPlotStartX = margin.left + averageValueLabelHorizontalGap;
    const barPlotWidth = plotWidth - (averageValueLabelHorizontalGap * 2);

    function isValidChartValue(value: number | null | undefined) {
        return value !== null && value !== undefined && !Number.isNaN(value) && value !== 0;
    }

    function hasSingleCurrency(list: ComparePriceListItem[]) {
        const currencies = list
            .map((item) => item.currency)
            .filter((currency) => currency !== null && currency !== undefined && currency !== "");

        return currencies.length > 0 && new Set(currencies).size === 1;
    }

    function formatValue(value: number) {
        return Number.isFinite(value) ? value.toFixed(2) : "0.00";
    }

    function valueLabelBackgroundWidth(value: number) {
        return formatValue(value).length * 7 + 8;
    }

    function weightedAverage(items: ComparePriceListItem[], priceKey: "price" | "priceEur", promoPriceKey: "pricePromo" | "pricePromoEur") {
        const priceSource = items.filter((item) => item.purchasedInd === 1 && isValidChartValue(item[priceKey]) && isValidChartValue(item.purchasedVol));
        const promoSource = items.filter((item) => item.purchasedInd === 1 && isValidChartValue(item[promoPriceKey]) && isValidChartValue(item.purchasedVolPromo));
        const totalWeight = priceSource.reduce((sum, item) => sum + item.purchasedVol, 0) + promoSource.reduce((sum, item) => sum + item.purchasedVolPromo, 0);

        if (!totalWeight) {
            return 0;
        }

        const weightedPriceSum = priceSource.reduce((sum, item) => sum + item[priceKey] * item.purchasedVol, 0);
        const weightedPromoSum = promoSource.reduce((sum, item) => sum + item[promoPriceKey] * item.purchasedVolPromo, 0);

        return (weightedPriceSum + weightedPromoSum) / totalWeight;
    }

    function average(items: ComparePriceListItem[], priceKey: "price" | "priceEur", promoPriceKey: "pricePromo" | "pricePromoEur") {
        const source = items
            .map((item) => isValidChartValue(item[promoPriceKey]) ? item[promoPriceKey] : item[priceKey])
            .filter((value) => isValidChartValue(value));

        if (!source.length) {
            return 0;
        }

        return source.reduce((sum, value) => sum + value, 0) / source.length;
    }

    function barFill(isPromo: boolean, isOnlyOffer: boolean) {
        if (isOnlyOffer) {
            return isPromo ? "#fcd34d" : "#f59e0b";
        }

        return isPromo ? "#60a5fa" : "var(--tesco-blue)";
    }

    function isFilledBar(item: ComparePriceListItem, isPromo: boolean) {
        if (item.onlyOfferInd === 1 || item.purchasedInd !== 1) {
            return false;
        }

        const volume = isPromo ? item.purchasedVolPromo : item.purchasedVol;
        return volume !== null && volume !== undefined && !Number.isNaN(volume) && volume > 0;
    }

    $: console.log("priceList", {... priceList});
    $: useNativeCurrency = hasSingleCurrency(priceList);
    $: valueKey = useNativeCurrency ? "price" : "priceEur";
    $: promoValueKey = useNativeCurrency ? "pricePromo" : "pricePromoEur";
    $: currencyLabel = useNativeCurrency && priceList.length ? priceList[0].currency : "EUR";

    $: totalPurchasedVolume = priceList.reduce(
        (sum, item) => sum + (isValidChartValue(item.purchasedVol) ? item.purchasedVol : 0) + (isValidChartValue(item.purchasedVolPromo) ? item.purchasedVolPromo : 0),
        0,
    );

    $: chartData = [...priceList]
        .sort((a, b) => (a.onlyOfferInd === 1 ? 1 : 0) - (b.onlyOfferInd === 1 ? 1 : 0))
        .map((item) => {
            const priceValue = item[valueKey] ?? 0;
            const promoValue = item[promoValueKey] ?? 0;
            const priceShare = totalPurchasedVolume > 0 && isValidChartValue(item.purchasedVol)
                ? `${Math.round((item.purchasedVol / totalPurchasedVolume) * 100)}%`
                : "";
            const promoShare = totalPurchasedVolume > 0 && isValidChartValue(item.purchasedVolPromo)
                ? `${Math.round((item.purchasedVolPromo / totalPurchasedVolume) * 100)}%`
                : "";

            return {
                ...item,
                priceValue,
                promoValue,
                hasPrice: isValidChartValue(priceValue),
                hasPromoPrice: isValidChartValue(promoValue),
                isOnlyOffer: item.onlyOfferInd === 1,
                filledPrice: isFilledBar(item, false),
                filledPromoPrice: isFilledBar(item, true),
                priceShare,
                promoShare,
            };
        });
    $: avgValue = average(priceList, valueKey, promoValueKey);
    $: weightedAvgValue = weightedAverage(priceList, valueKey, promoValueKey);

    $: maxValue = Math.max(
        0,
        ...chartData.flatMap((item) => [item.hasPrice ? item.priceValue : 0, item.hasPromoPrice ? item.promoValue : 0]),
        avgValue,
        weightedAvgValue,
    );

    $: yMax = maxValue > 0 ? maxValue * 1.1 : 1;
    $: supplierCount = Math.max(chartData.length, 1);
    $: groupWidth = barPlotWidth / supplierCount;
    $: innerGap = Math.max(4, Math.min(8, groupWidth * 0.08));
    $: outerGap = Math.max(10, Math.min(24, groupWidth * 0.16));
    $: drawableGroupWidth = Math.max(groupWidth - outerGap, 12);
    $: preferredBarWidth = 60;
    $: maxBarWidthThatFits = Math.max(8, (drawableGroupWidth - innerGap) / 2);
    $: barWidth = Math.min(preferredBarWidth, maxBarWidthThatFits);

    function yScale(value: number) {
        //console.log("==============  yScale  ================", value, yMax, margin.top, plotHeight);
        return margin.top + plotHeight - (value / yMax) * plotHeight;
    }

    function xPositions(index: number, hasPromoPrice: boolean) {
        const groupX = barPlotStartX + index * groupWidth;
        const totalBarsWidth = hasPromoPrice ? (barWidth * 2 + innerGap) : barWidth;
        const startX = groupX + (groupWidth - totalBarsWidth) / 2;

        return {
            groupCenter: groupX + groupWidth / 2,
            priceX: startX,
            promoX: startX + barWidth + innerGap,
        };
    }

    $: yTicks = Array.from({ length: yTickCount + 1 }, (_, index) => {
        //console.log("==============  yTicks  ================");
        const value = (yMax / yTickCount) * index;
        return {
            value,
            y: yScale(value),
        };
    });
</script>

<Dialog
    data-qa="ps-add-qs-dialog"
    bind:open
    scrimClickAction=""
    escapeKeyAction=""
    aria-labelledby="data-changed-title"
    aria-describedby="data-changed-content"
    class="pa-2"
    surface$style={`width: ${chartWidth + legendWidth + chartGap + dialogPadding}px; max-width: min(calc(100vw - 32px), ${chartWidth + legendWidth + chartGap + dialogPadding}px);`}
>
    <DTitle id="data-changed-title" class={headerColors}>{$_("compare_prices") + ": " + title}</DTitle>
    <Content>
        <div class="chart-wrapper chart-wrapper-spaced">
            {#if chartData.length === 0}
                <div class="empty-state">No data</div>
            {:else}
                <div class="chart-layout">
                    <svg viewBox={`0 0 ${chartWidth} ${chartHeight}`} class="price-chart" role="img" aria-label={title}>
                        <defs>
                            <pattern id="priceHatch" patternUnits="userSpaceOnUse" width="8" height="8" patternTransform="rotate(135)">
                                <rect width="8" height="8" fill="var(--tesco-blue)"></rect>
                                <line x1="0" y1="0" x2="0" y2="8" stroke="#ffffff" stroke-width="1"></line>
                            </pattern>
                            <pattern id="promoHatch" patternUnits="userSpaceOnUse" width="8" height="8" patternTransform="rotate(135)">
                                <rect width="8" height="8" fill="#60a5fa"></rect>
                                <line x1="0" y1="0" x2="0" y2="8" stroke="#ffffff" stroke-width="1"></line>
                            </pattern>
                            <pattern id="offerPriceHatch" patternUnits="userSpaceOnUse" width="8" height="8" patternTransform="rotate(135)">
                                <rect width="8" height="8" fill="#f59e0b"></rect>
                                <line x1="0" y1="0" x2="0" y2="8" stroke="#ffffff" stroke-width="+"></line>
                            </pattern>
                            <pattern id="offerPromoHatch" patternUnits="userSpaceOnUse" width="8" height="8" patternTransform="rotate(135)">
                                <rect width="8" height="8" fill="#fcd34d"></rect>
                                <line x1="0" y1="0" x2="0" y2="8" stroke="#ffffff" stroke-width="+"></line>
                            </pattern>
                        </defs>

                        {#each yTicks as tick}
                            <line x1={margin.left} y1={tick.y} x2={chartWidth - margin.right} y2={tick.y} class="grid-line" />
                            <text x={margin.left - 10} y={tick.y + 4} text-anchor="end" class="axis-label">{formatValue(tick.value)}</text>
                        {/each}

                        <line x1={margin.left} y1={margin.top} x2={margin.left} y2={margin.top + plotHeight} class="axis-line" />
                        <line x1={margin.left} y1={margin.top + plotHeight} x2={chartWidth - margin.right} y2={margin.top + plotHeight} class="axis-line" />

                        {#if avgValue > 0}
                            <line
                                x1={margin.left}
                                y1={yScale(avgValue)}
                                x2={chartWidth - margin.right}
                                y2={yScale(avgValue)}
                                class="avg-line avg-line-value"
                            />
                        {/if}

                        {#if weightedAvgValue > 0}
                            <line
                                x1={margin.left}
                                y1={yScale(weightedAvgValue)}
                                x2={chartWidth - margin.right}
                                y2={yScale(weightedAvgValue)}
                                class="wavg-line wavg-line-value"
                            />
                        {/if}

                        {#if avgValue > 0}
                            <rect
                                x={chartWidth - margin.right - 6 - valueLabelBackgroundWidth(avgValue)}
                                y={yScale(avgValue) - 17}
                                width={valueLabelBackgroundWidth(avgValue)}
                                height="16"
                                rx="3"
                                class="avg-line-value-label-bg"
                            />
                            <text
                                x={chartWidth - margin.right - 10}
                                y={yScale(avgValue) - 5}
                                text-anchor="end"
                                class="avg-line-value-label avg-line-text"
                            >
                                {formatValue(avgValue)}
                            </text>
                        {/if}

                        {#if weightedAvgValue > 0}
                            <rect
                                x={margin.left + 6}
                                y={yScale(weightedAvgValue) - 17}
                                width={valueLabelBackgroundWidth(weightedAvgValue)}
                                height="16"
                                rx="3"
                                class="avg-line-value-label-bg"
                            />
                            <text
                                x={margin.left + 10}
                                y={yScale(weightedAvgValue) - 5}
                                text-anchor="start"
                                class="avg-line-value-label wavg-line-text"
                            >
                                {formatValue(weightedAvgValue)}
                            </text>
                        {/if}

                        {#each chartData as item, index}
                            {@const pos = xPositions(index, item.hasPromoPrice)}
                            {@const priceHeight = item.hasPrice ? ((item.priceValue / yMax) * plotHeight) : 0}
                            {@const promoHeight = item.hasPromoPrice ? ((item.promoValue / yMax) * plotHeight) : 0}
                            {@const priceY = margin.top + plotHeight - priceHeight}
                            {@const promoY = margin.top + plotHeight - promoHeight}
                            {@const valueLabelOffset = 6}

                            {#if item.hasPrice}
                                <rect
                                    x={pos.priceX}
                                    y={priceY}
                                    width={barWidth}
                                    height={priceHeight}
                                    rx="2"
                                    fill={item.filledPrice ? barFill(false, item.isOnlyOffer) : (item.isOnlyOffer ? "url(#offerPriceHatch)" : "url(#priceHatch)")}
                                    class:only-offer-bar={item.isOnlyOffer}
                                >
                                    <title>{`${item.supplierName} - ${$_("price")}: ${formatValue(item.priceValue)} ${currencyLabel}`}</title>
                                </rect>
                                <rect
                                    x={pos.priceX + barWidth / 2 - valueLabelBackgroundWidth(item.priceValue) / 2}
                                    y={Math.max(margin.top + 12, priceY - valueLabelOffset) - 12}
                                    width={valueLabelBackgroundWidth(item.priceValue)}
                                    height="16"
                                    rx="3"
                                    class="bar-value-label-bg"
                                />
                                <text
                                    x={pos.priceX + barWidth / 2}
                                    y={Math.max(margin.top + 12, priceY - valueLabelOffset)}
                                    text-anchor="middle"
                                    class="bar-value-label"
                                >
                                    {formatValue(item.priceValue)}
                                </text>
                                {#if item.priceShare}
                                    <text
                                        x={pos.priceX + barWidth / 2}
                                        y={margin.top + plotHeight - 8}
                                        text-anchor="middle"
                                        class="bar-share-label"
                                    >
                                        {item.priceShare}
                                    </text>
                                {/if}
                            {/if}

                            {#if item.hasPromoPrice}
                                <rect
                                    x={pos.promoX}
                                    y={promoY}
                                    width={barWidth}
                                    height={promoHeight}
                                    rx="2"
                                    fill={item.filledPromoPrice ? barFill(true, item.isOnlyOffer) : (item.isOnlyOffer ? "url(#offerPromoHatch)" : "url(#promoHatch)")}
                                    class:only-offer-bar={item.isOnlyOffer}
                                >
                                    <title>{`${item.supplierName} - ${$_("promo_price")}: ${formatValue(item.promoValue)} ${currencyLabel}`}</title>
                                </rect>
                                <rect
                                    x={pos.promoX + barWidth / 2 - valueLabelBackgroundWidth(item.promoValue) / 2}
                                    y={Math.max(margin.top + 12, promoY - valueLabelOffset) - 12}
                                    width={valueLabelBackgroundWidth(item.promoValue)}
                                    height="16"
                                    rx="3"
                                    class="bar-value-label-bg"
                                />
                                <text
                                    x={pos.promoX + barWidth / 2}
                                    y={Math.max(margin.top + 12, promoY - valueLabelOffset)}
                                    text-anchor="middle"
                                    class="bar-value-label"
                                >
                                    {formatValue(item.promoValue)}
                                </text>
                                {#if item.promoShare}
                                    <text
                                        x={pos.promoX + barWidth / 2}
                                        y={margin.top + plotHeight - 8}
                                        text-anchor="middle"
                                        class="bar-share-label"
                                    >
                                        {item.promoShare}
                                    </text>
                                {/if}
                            {/if}

                            <text
                                x={pos.groupCenter}
                                y={chartHeight - margin.bottom + 22}
                                text-anchor="middle"
                                class:only-offer-label={item.isOnlyOffer}
                                class="supplier-label"
                            >
                                {item.supplierName}
                            </text>
                        {/each}

                        <text
                            x={margin.left - 52}
                            y={margin.top + plotHeight / 2}
                            text-anchor="middle"
                            transform={`rotate(-90 ${margin.left - 52} ${margin.top + plotHeight / 2})`}
                            class="axis-title"
                        >
                            {currencyLabel}
                        </text>
                    </svg>
                    <div class="legend legend-vertical">
                        <div class="legend-item"><span class="legend-box filled"></span><span>{$_("purchase_price")}</span></div>
                        <div class="legend-item"><span class="legend-box hatched"></span><span>{$_("quote_sheet_price")}</span></div>
                        <div class="legend-item"><span class="legend-box price"></span><span>{$_("price")}</span></div>
                        <div class="legend-item"><span class="legend-box promo"></span><span>{$_("promo_price")}</span></div>
                        <div class="legend-item"><span class="legend-box offer"></span><span>{$_("only_offer")}</span></div>
                        {#if weightedAvgValue > 0}
                            <div class="legend-item"><span class="legend-line wavg-solid wavg-value"></span><span>{$_("wavg_price")}</span></div>
                        {/if}
                        <div class="legend-item"><span class="legend-line avg-dashed avg-value"></span><span>{$_("avg_price")}</span></div>
                    </div>
                </div>
            {/if}
        </div>
    </Content>
    <div class="dialog-actions-centered">
        <Actions>
            <Button action="close" class="tescored tescored-text outlined" on:click={() => { open = false; }}>
                <Label>{$_("close")}</Label>
            </Button>
        </Actions>
    </div>
</Dialog>

<style>
    .chart-wrapper {
        width: fit-content;
        min-width: 100%;
    }

    .chart-wrapper-spaced {
        margin-top: 32px;
        margin-bottom: 0;
    }

    :global(.dialog-content-chart .mdc-dialog__content) {
        padding-top: 0;
        padding-bottom: 0;
    }

    .empty-state {
        display: flex;
        justify-content: center;
        align-items: center;
        min-height: 240px;
        color: #6b7280;
    }

    .legend {
        display: flex;
        flex-wrap: wrap;
        gap: 12px 20px;
        margin-bottom: 8px;
        font-size: 0.875rem;
    }

    .legend-vertical {
        display: flex;
        flex-direction: column;
        flex-wrap: nowrap;
        gap: 10px;
        margin-bottom: 0;
        min-width: 220px;
        width: 220px;
        padding-top: 16px;
        flex-shrink: 0;
    }

    .legend-item {
        display: inline-flex;
        align-items: center;
        gap: 8px;
    }

    .legend-box {
        width: 14px;
        height: 14px;
        border-radius: 3px;
        display: inline-block;
    }

    .legend-box.price {
        background: var(--tesco-blue);
    }

    .legend-box.promo {
        background: #60a5fa;
    }

    .legend-box.filled {
        background: #cbd5e1;
        border: 1px solid #94a3b8;
    }

    .legend-box.hatched {
        background: repeating-linear-gradient(45deg, #ffffff 0 4px, #cbd5e1 2px 8px);
        border: 1px solid #94a3b8;
    }

    .legend-box.offer {
        background: repeating-linear-gradient(45deg, #f59e0b 0 4px, #fde68a 4px 8px);
    }

    .legend-line {
        width: 18px;
        height: 0;
        border-top-width: 3px;
        display: inline-block;
    }

    .legend-line.avg-dashed {
        border-top-style: dashed;
    }

    .legend-line.wavg-solid {
        border-top-style: solid;
    }

    .legend-line.avg-value {
        border-top-color: var(--tesco-blue);
    }

    .legend-line.wavg-value {
        border-top-color: var(--tesco-blue);
    }

    .price-chart {
        width: 980px;
        min-width: 980px;
        height: auto;
        overflow: visible;
        flex-shrink: 0;
    }

    .grid-line {
        stroke: #e5e7eb;
        stroke-width: 1;
    }

    .axis-line {
        stroke: #6b7280;
        stroke-width: 1.2;
    }

    .avg-line {
        stroke-width: 1;
        stroke-dasharray: 6 4;
    }

    .wavg-line {
        stroke-width: 1;
    }

    .avg-line-value {
        stroke: var(--tesco-blue);
    }

    .wavg-line-value {
        stroke: var(--tesco-blue);
    }

    .avg-line-text,
    .wavg-line-text {
        fill: #374151;
        stroke: none;
    }

    .bar-share-label {
        fill: #ffffff;
        stroke: none;
        font-size: 12px;
        font-weight: 600;
    }

    .axis-label,
    .axis-title,
    .supplier-label,
    .bar-value-label {
        fill: #374151;
    }

    .avg-line-value-label {
        fill: var(--tesco-blue);
        font-size: 12px;
        font-weight: 600;
    }

    .avg-line-value-label-bg,
    .bar-value-label-bg {
        fill: #ffffff;
    }

    .supplier-label {
        font-size: 12px;
    }

    .bar-value-label {
        font-size: 12px;
        font-weight: 600;
    }

    .only-offer-bar {
        stroke: #92400e;
        stroke-width: 1.5;
        stroke-dasharray: 4 2;
    }

    .only-offer-label {
        fill: #92400e;
        font-style: italic;
    }


    .chart-layout {
        display: inline-flex;
        align-items: stretch;
        gap: 24px;
        width: fit-content;
    }

    .dialog-actions-centered {
        display: flex;
        justify-content: center;
        width: 100%;
        padding-right: 0;
        margin-top: -28px;
        padding-bottom: 10px;
    }


</style>
